import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_GENERAL } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId, ApplicationControlStartRuleItem } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  ApplicationControlFormValue,
  ApplicationControlSearchableKeywords,
} from '../../../../utils/uiSettings/applicationControl/application-control.types';
import {
  APPLICATION_CONTROL_DEFAULT_VALUE,
  getApplicationControlSearchableKeywords,
} from '../../../../utils/uiSettings/applicationControl/application-control.values';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzApplicationControlRulesGridWebComponent } from '../gridComponents/GzApplicationControlRulesGridWebComponent';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_GENERAL)
export class GzApplicationControlGeneralWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  ApplicationControlFormValue,
  ApplicationControlSearchableKeywords
> {
  /**
   * Path for Application Control module.
   */
  protected path = {
    moduleId: ModuleId.APPLICATION_CONTROL,
    sectionId: SectionId.NONE,
  };

  /**
   * Searchable keywords for Application Control section.
   */
  protected searchableKeywords = getApplicationControlSearchableKeywords();

  @query('#application-control-toggle')
  private applicationControlToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#application-control-info-text')
  private applicationControlInfoText: GzTextWebComponent;

  @query('#application-control-content')
  private applicationControlContent: HTMLElement;

  // test mode (report only) checkbox
  @query('#report-only-checkbox-form-field')
  private reportOnlyCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#rules-grid-form-field')
  private rulesGridFormField: GzFormFieldWebComponent<
    GzApplicationControlRulesGridWebComponent,
    ApplicationControlStartRuleItem[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-form-field id="application-control-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[ApplicationControlSearchableKeywords.ApplicationControl].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[ApplicationControlSearchableKeywords.ApplicationControl].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="application-control-info-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      class="infoText"
      >${translateFn(
        'Application Control offers protection against malware threats by blocking unauthorized applications and processes.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="application-control-content" class="mb-1">
        <div class="section mt-3">
          <gz-title
            searchable-label="${this.searchableKeywords[ApplicationControlSearchableKeywords.TestMode].label}"
            fontSize="11"
            fontWeight="600"
            class="form-subtitle mb-1"
            >${this.searchableKeywords[ApplicationControlSearchableKeywords.TestMode].translatedLabel}</gz-title
          >
          <gz-text id="application-control-test-mode-info-text" fontSize="12" fontWeight="400" class="infoText mb-3"
            >${translateFn(
              'When running in Test Mode, Application Control only reports the processes that would have been blocked, without actually blocking them.'
            )}
          </gz-text>
          <gz-form-field id="report-only-checkbox-form-field" class="mb-6">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[ApplicationControlSearchableKeywords.RunInTestMode].label}"
              gz-form-input
              label="${this.searchableKeywords[ApplicationControlSearchableKeywords.RunInTestMode].translatedLabel}"
            ></gz-checkbox>
          </gz-form-field>
          <gz-title
            id="application-control-rules-title"
            searchable-label="${this.searchableKeywords[ApplicationControlSearchableKeywords.ProcessStartRules].label}"
            fontSize="11"
            fontWeight="600"
            class="form-subtitle"
            >${this.searchableKeywords[ApplicationControlSearchableKeywords.ProcessStartRules].translatedLabel}
          </gz-title>

          <gz-form-field id="rules-grid-form-field">
            <gz-policy-panel-application-control-rules-grid
              class="removeGridPadding"
              gz-form-input
            ></gz-policy-panel-application-control-rules-grid>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.applicationControl
   */
  protected getValueFromManager(): ApplicationControlFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.APPLICATION_CONTROL]: APPLICATION_CONTROL_DEFAULT_VALUE,
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.APPLICATION_CONTROL]: PolicyPanelManager.getInstance().getPolicy('uiSettings.applicationControl'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        applicationControl: {
          general: {
            enable: this.applicationControlToggle,
            reportOnly: this.reportOnlyCheckboxFormField,
          },
          startRules: this.rulesGridFormField,
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        applicationControl: {
          general: {
            enable: this.valueT.uiSettings.applicationControl.general.enable,
            reportOnly: this.valueT.uiSettings.applicationControl.general.reportOnly,
          },
          startRules: this.valueT.uiSettings.applicationControl.startRules,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'applicationControl'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.applicationControlInfoText, !event.detail.checked);
    toggleDisabled(this.applicationControlContent, !event.detail.checked);

    // enable/disable grid empty state button
    const gridEmptyStateButton = this.rulesGridFormField.Input.shadowRoot?.getElementById('policies-grid-empty-state');
    if (event.detail.checked) {
      gridEmptyStateButton?.classList.remove('disabled');
    } else {
      gridEmptyStateButton?.classList.add('disabled');
    }
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.rulesGridFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_APPLICATION_CONTROL_GENERAL: GzApplicationControlGeneralWebComponent;
  }
}
